import { debounce } from 'lodash';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from 'react';
import { handleTrack } from '../../../../utils/tracker';
import { useOnClickOutside } from '../../../hooks';
import { NewToCContainer } from '../styles';
import { IndexList } from './index_list';

// Constants for magic numbers
const SCROLL_THRESHOLD = 100;
const SCROLL_DEBOUNCE_MS = 100;
const DROPDOWN_TOP_POSITION = '94px';
const HEADER_TOP_POSITION = 'top-16';

// Type definitions
interface TocItem {
  id: string;
  content: string;
}

interface MobileTocAIProps {
  tocIndexList: TocItem[];
  dom?: HTMLElement | null;
}

export const MobileTocAI = ({
  tocIndexList,
  dom,
}: MobileTocAIProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<string>('h-1');
  const [toggleIndex, setToggleIndex] = useState(false);
  const [activeId, setActiveId] = useState<number>(-1);

  // Memoized helper function to extract ID from index string
  const extractId = useCallback((activeIndex: string): number => {
    const id = Number(activeIndex.slice(1));
    return isNaN(id) ? -1 : id;
  }, []);

  // Update activeId when activeIndex changes
  useEffect(() => {
    const id = extractId(activeIndex);
    setActiveId(id);
  }, [activeIndex, extractId]);

  // Optimized toggle handler with deferred tracking
  const toggleDropDown = useCallback(() => {
    // Immediate UI update for instant feedback
    setToggleIndex((prev) => {
      const newState = !prev;

      // Defer tracking to avoid blocking the main thread
      if (newState) {
        // Use setTimeout to defer non-critical tracking
        setTimeout(() => {
          handleTrack({
            label: 'content_index_opened',
          });
        }, 0);
      }

      return newState;
    });
  }, []);

  // Close dropdown when clicking outside
  useOnClickOutside(ref, () => {
    setToggleIndex(false);
  });

  // Memoized scroll handler with startTransition for non-urgent updates
  const handleScroll = useCallback(() => {
    if (window.scrollY < SCROLL_THRESHOLD) {
      // Use startTransition for non-urgent state updates
      startTransition(() => {
        setActiveId(-1);
        setActiveIndex('h-1');
      });
    }
  }, []);

  // Setup scroll listener with debounce
  useEffect(() => {
    const debouncedScroll = debounce(handleScroll, SCROLL_DEBOUNCE_MS, {
      leading: false,
      trailing: true,
    });

    window.addEventListener('scroll', debouncedScroll, { passive: true });

    return () => {
      debouncedScroll.cancel();
      window.removeEventListener('scroll', debouncedScroll);
    };
  }, [handleScroll]);

  // Optimized tracking handler - deferred to not block interaction
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    // Defer tracking to avoid blocking paint
    const target = e.target;
    const track = () => {
      handleTrack({
        label: 'lhs_cta',
        obj: target,
      });
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(track, { timeout: 2000 });
      return;
    }

    setTimeout(track, 0);
  }, []);

  // Get current title with bounds checking
  const currentTitle = useMemo(() => {
    if (activeId === -1) {
      return 'Index';
    }
    return tocIndexList[activeId]?.content || 'Index';
  }, [activeId, tocIndexList]);

  // Optimized keyboard handler
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleDropDown();
      }
    },
    [toggleDropDown]
  );

  // Early return if no DOM element
  if (!dom) {
    return <></>;
  }

  return (
    <NewToCContainer onClick={handleContainerClick}>
      <div ref={ref}>
        {/* Header/Toggle Button */}
        <div
          className={`flex items-center gap-1 bg-grey-150 px-5 py-1.5 fixed ${HEADER_TOP_POSITION} z-10 w-full`}
          onClick={toggleDropDown}
          role="button"
          aria-expanded={toggleIndex}
          aria-label="Toggle table of contents"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <h4
            className="overflow-hidden text-ellipsis whitespace-nowrap flex-1"
            style={{
              contain: 'layout style paint',
              contentVisibility: 'auto',
            }}
          >
            {currentTitle}
          </h4>
          <img
            className={`w-4 h-4 transition-transform duration-200 ${
              toggleIndex ? 'rotate-180' : ''
            }`}
            src="https://assets.clear.in/cleartax/images/1710323832_layer2.svg"
            alt="dropdown-arrow"
            aria-hidden="true"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Dropdown Content - Only render when open for better performance */}
        {toggleIndex && (
          <div
            className="overflow-y-auto rounded-b-xl border max-h-[320px] bg-white w-full z-10 fixed"
            style={{
              boxShadow: '6px 36px 108px -20px rgba(7,36,75,0.66)',
              top: DROPDOWN_TOP_POSITION,
              contain: 'layout style paint',
              willChange: toggleIndex ? 'contents' : 'auto',
            }}
            role="navigation"
            aria-label="Table of contents"
          >
            <div className="TOC_index_Mobile TOC_index">
              <IndexList
                tocIndexList={tocIndexList}
                dom={dom}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                setToggleIndex={setToggleIndex}
              />
            </div>
          </div>
        )}
      </div>
    </NewToCContainer>
  );
};
